import { StatusDot, type StatusDotSpec } from '@/components/StatusDot';
import type { FC } from 'react';
import { Link, useMatch, useResolvedPath } from 'react-router-dom';

export type TabNavItem = {
    label: string;
    /** Path for the tab's Link — absolute, or relative to the current route. */
    to: string;
    /** Optional count badge appended to the label. */
    count?: number;
    /** A status dot after the label: a Tailwind background class plus what it means, for the title. */
    dot?: StatusDotSpec;
};

/**
 * URL-driven tab strip (the rock-admin convention: tabs are routes, not local state). Active styling
 * matches the original HomeDetailsPage tab bar; each tab is a real Link so cmd+click works. Tab
 * switches replace the history entry so Back leaves the page instead of walking through tabs.
 */
export const TabNav: FC<{ tabs: TabNavItem[] }> = ({ tabs }) => (
    <div className="flex gap-2 overflow-x-auto border-b">
        {tabs.map((tab) => (
            <Tab key={tab.to} tab={tab} />
        ))}
    </div>
);

const Tab: FC<{ tab: TabNavItem }> = ({ tab }) => {
    const resolved = useResolvedPath(tab.to);
    const isActive = !!useMatch({ path: `${resolved.pathname}/*` });
    return (
        <Link
            to={tab.to}
            replace
            className={`shrink-0 whitespace-nowrap px-4 py-2 border-b-2 transition-colors ${
                isActive
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
        >
            {tab.label}
            {tab.count !== undefined && <span className="ml-1.5 text-xs text-muted-foreground">({tab.count})</span>}
            {tab.dot && <StatusDot dot={tab.dot} className="ml-1.5" />}
        </Link>
    );
};
